import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LocationType, ProviderTokenTypes, SAVED_LOCATION } from '@guryou/core';
import { GoogleMap, GoogleMapsAutocomplete, HiddenField } from '@guryou/html-components';
import {
  generateZoom,
  getLatestZoomMeeting,
  getProviderAddresses,
  getProviderTypeTokens,
  stateIsLoaded,
  stateIsLoading,
  useEffectOnError,
  useEffectOnSuccess,
  zoomCheckIfConnected,
} from '@guryou/redux';
import { parseGoogleMapLocation, parseGoogleMapPlace } from '@guryou/utils';

import { SystemButton } from './ActionButton';
import { dealTokens } from './designTokens';
import { SystemCheckbox, SystemFieldControl, SystemFieldError, SystemSelect, SystemTextArea, SystemTextInput } from './SystemFormControls';

// Design-system copy of ./LocationPicker.js (plus its ./LocationUrl.js and
// ./LocationUrlWithZoom.js sub-forms, inlined below). Same props, same behaviour and —
// the important part — the SAME submitted field names (`locationType`, `externalProvider`,
// `url`, `locationNote`, `latitude`, `longitude`, `address`, `propagateLocation`), so any
// legacy `Form` that renders ./LocationPicker.js can swap to this one without touching its
// onSubmit. The original and its ~70 call sites are untouched; this is being tried in
// containers/newDesigns/CreateReservation.js first.
//
// What changed is only the chrome: the legacy PaperBlock + MUI notched/floating-label
// fields became label-above System* controls in one vertical stack; the map sits full
// width in a rounded bordered frame (was a fixed 25vw box capped at `sizes.small`); the
// Zoom "get last / create meeting" pair became ghost SystemButtons with their explanation
// as the tooltip; the Zoom error uses SystemFieldError instead of a red DefaultText. The
// layout props that only existed to fight the legacy grid (`xs`, `noBorder`, `fullWidth`)
// are accepted and ignored — every field is full width here.
const defineSelectedViewValue = (location, externalProvider) => {
  if (location?.address) {
    return LocationType.mapLocation;
  } else if (!externalProvider && (location?.note || location?.url)) {
    return LocationType.plainNote;
  } else if (externalProvider && ProviderTokenTypes.google === externalProvider) {
    return LocationType.google;
  } else if (externalProvider && ProviderTokenTypes.zoom === externalProvider) {
    return LocationType.zoom;
  }
  return LocationType.providerLocation;
};

const externalProviderLabel = type => `${type.charAt(0).toUpperCase() + type.slice(1)}${type === ProviderTokenTypes.google ? ' Meet' : ''}`;

const noteValue = locationObject => locationObject.locationNote || locationObject.note || '';

// ./LocationUrl.js — URL + note.
const OnlineFields = ({ locationObject, disabled, url, note }) => {
  const { t } = useTranslation();
  return (
    <>
      <SystemTextInput
        key={`url_${url}`}
        disabled={disabled}
        label="URL"
        name="url"
        value={url ?? (locationObject.url || noteValue(locationObject))}
      />
      <SystemTextArea
        key={`note_${note}`}
        disabled={disabled}
        rows={3}
        label={t('GENERAL.leaveNote')}
        placeholder={t('GENERAL.enterValidUrl')}
        name="locationNote"
        value={note ?? noteValue(locationObject)}
      />
    </>
  );
};

// ./LocationUrlWithZoom.js — same URL + note, prefilled from a generated or the latest
// Zoom meeting. Same redux calls and the same guards on both buttons.
const OnlineFieldsWithZoom = ({ locationObject, disabled }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [clickedOnce, setClickedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [zoomLink, setZoomLink] = useState('');
  const [zoomNote, setZoomNote] = useState(null);
  const zoomUrl = useSelector(state => state.zoom.generate);
  const latestMeeting = useSelector(state => state.zoom.latestZoomMeeting);
  const isConnected = useSelector(state => state.zoom.isConnected);

  useEffect(() => {
    dispatch(zoomCheckIfConnected());
  }, [dispatch]);

  useEffectOnError(
    state => state.zoom.generate,
    () => setError(t('ZOOM.errorZoom'))
  );

  useEffectOnSuccess(
    state => state.zoom.generate,
    data => {
      if (data) {
        setZoomLink(data);
        setZoomNote('Zoom url');
      }
    }
  );

  useEffectOnSuccess(
    state => state.zoom.latestZoomMeeting,
    data => {
      if (data) {
        setZoomLink(data.joinUrl);
        setZoomNote('Zoom url');
      }
    }
  );

  const connectionLoading = stateIsLoading([isConnected]);
  const notConnected = isConnected?.data === false;

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: dealTokens.space[2] }}>
        <SystemButton
          variant="ghost"
          size="sm"
          tooltip={t('ZOOM.getLastExplanation')}
          onClick={() => dispatch(getLatestZoomMeeting())}
          disabled={disabled || connectionLoading || notConnected || stateIsLoading([latestMeeting])}
        >
          {t('ZOOM.getLastMeeting')}
        </SystemButton>
        <SystemButton
          variant="ghost"
          size="sm"
          tooltip={t('ZOOM.createExplanation')}
          onClick={() => {
            setClickedOnce(true);
            dispatch(generateZoom());
          }}
          disabled={disabled || connectionLoading || clickedOnce || notConnected || stateIsLoading([zoomUrl])}
        >
          {t('ZOOM.createMeeting')}
        </SystemButton>
      </div>
      <SystemFieldError>{error}</SystemFieldError>
      <OnlineFields locationObject={locationObject} disabled={disabled} url={zoomLink || undefined} note={zoomNote || undefined} />
    </>
  );
};

const SystemLocationPicker = ({
  showCheckBox,
  checkBoxLabel,
  location,
  externalProvider,
  disabled = false,
  includeZoom = false,
  fromCalendar = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mapsAutocomplete = useRef();
  // GoogleMap (@guryou/html-components) registers its Places `place_changed` listener once,
  // on first mount, so it keeps calling the `onPlaceSelected` from THAT render. Reading
  // `disabled` straight from the closure froze it at its mount-time value — and CreateReservation
  // remounts this picker (key = sub-service) before availability loads, i.e. while disabled —
  // so every later suggestion pick was ignored. The map callbacks read this ref instead.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const [locationObject, setLocationObject] = useState(location || {});
  const [selectedView, setSelectedView] = useState(defineSelectedViewValue(location, externalProvider));
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [propagate, setPropagate] = useState(false);
  const providerTypeTokens = useSelector(state => state.provider.providerTypeTokens);
  const addressesState = useSelector(state => state.addresses.all);

  useEffect(() => {
    if (!stateIsLoaded(addressesState)) {
      dispatch(getProviderAddresses(true));
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // The Google Meet / Zoom type options come from the provider's CONNECTED integrations
  // (getProviderTypeTokens — providers_tokens rows with a live access + refresh token).
  // ./LocationPicker.js only read them from the store, and only the calendar and the
  // Services/Courses catalogues ever fetched them, so after a refresh on e.g. a wizard
  // page the options silently vanished. Fetched here when missing, like the addresses.
  useEffect(() => {
    if (!stateIsLoaded(providerTypeTokens) && !stateIsLoading(providerTypeTokens)) {
      dispatch(getProviderTypeTokens());
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const homeAddresses = stateIsLoaded(addressesState) ? (addressesState.data || []).filter(a => a.type === 'business') : [];

  const locationTypeData = useMemo(() => {
    const arr = [
      { label: t('GENERAL.providerLocation'), value: LocationType.providerLocation },
      { label: t('GENERAL.savedLocation'), value: SAVED_LOCATION },
      { label: t('GENERAL.chooseLocationFromMap'), value: LocationType.mapLocation },
      { label: t('GENERAL.onlineLocation'), value: LocationType.plainNote },
    ];
    if (providerTypeTokens?.data?.length) {
      return [...arr, ...providerTypeTokens.data.map(x => ({ label: externalProviderLabel(x?.type || ''), value: LocationType[x?.type] }))];
    }
    if (externalProvider) {
      return [...arr, { label: externalProviderLabel(externalProvider), value: LocationType[externalProvider] }];
    }
    return arr;
  }, [t, providerTypeTokens?.data, externalProvider]);

  const coordinateFields = (
    <>
      <HiddenField name="latitude" value={locationObject.latitude || ''} />
      <HiddenField name="longitude" value={locationObject.longitude || ''} />
      <HiddenField name="address" value={locationObject.address || ''} />
    </>
  );

  // Two lock-outs, both inherited from ./LocationPicker.js: `disabled` (callers pass it
  // until the reservation has availability — CreateReservation disables every field until
  // a service, sub-service and resource have produced personnelDateSlots) disables the
  // whole picker; a Zoom/Meet service opened from the calendar locks only the type, since
  // the service's own external provider decides it. The label-above System* fields don't
  // grey their own label the way the legacy notched MUI fields did, so the locked part is
  // dimmed here — same 0.6 opacity SystemCheckbox uses for its disabled state.
  const typeLocked = disabled || (!!externalProvider && fromCalendar);
  const dimmed = { opacity: 0.6, cursor: 'not-allowed' };

  return (
    <div
      aria-disabled={disabled || undefined}
      style={{ display: 'flex', flexDirection: 'column', gap: dealTokens.space[4], width: '100%', minWidth: 0, ...(disabled ? dimmed : null) }}
    >
      <div style={!disabled && typeLocked ? dimmed : undefined}>
        <SystemSelect
          disabled={typeLocked}
          value={selectedView}
          label={t('GENERAL.location')}
          name="locationType"
          none={false}
          options={locationTypeData}
          onChange={val => {
            setSelectedView(val);
            if (val !== SAVED_LOCATION) {
              setSelectedAddressId('');
              setLocationObject({});
            }
          }}
        />
      </div>
      {selectedView === LocationType.google && <HiddenField name="externalProvider" value={ProviderTokenTypes.google} />}
      {selectedView === LocationType.zoom && <HiddenField name="externalProvider" value={ProviderTokenTypes.zoom} />}

      {selectedView === LocationType.plainNote &&
        (includeZoom ? (
          <OnlineFieldsWithZoom locationObject={locationObject} disabled={disabled} />
        ) : (
          <OnlineFields locationObject={locationObject} disabled={disabled} />
        ))}

      {selectedView === LocationType.mapLocation && (
        <>
          {coordinateFields}
          <SystemFieldControl label={t('GENERAL.address', 'Address')} name="mapsAutocomplete">
            <GoogleMapsAutocomplete
              fullWidth
              margin="none"
              disabled={disabled}
              inputRef={mapsAutocomplete}
              value={locationObject?.address || ''}
              // GoogleMapsAutocomplete calls onBlur with NO event, so `e.target.value` was always
              // empty and every blur — including the one fired when a suggestion is clicked —
              // wiped the address. Check the real input instead.
              onBlur={() => !mapsAutocomplete.current?.value && setLocationObject({})}
              onChange={e => {
                const address = e?.target?.value;
                setLocationObject(prev => (address ? { ...prev, address } : {}));
              }}
              placeholder={t('GENERAL.enterLocation')}
              InputProps={{ className: 'studio-input' }}
              required
            />
          </SystemFieldControl>
          {/* `disabled` never reached the map in ./LocationPicker.js either: its click and
              place-selected callbacks rewrote the hidden lat/lng/address while every other
              field was locked. Both are ignored while disabled, and pointer-events are off
              on the frame so the map can't even be panned or clicked. */}
          <div
            aria-disabled={disabled || undefined}
            style={{
              overflow: 'hidden',
              borderRadius: dealTokens.radius.lg,
              border: `1px solid ${dealTokens.color.border}`,
              pointerEvents: disabled ? 'none' : undefined,
            }}
          >
            <GoogleMap
              style={{ height: 240, width: '100%' }}
              defaultCenter={{ lat: locationObject.latitude ?? 41.9028, lng: locationObject.longitude ?? 12.4964 }}
              autocompleteRef={mapsAutocomplete}
              onMapClick={point => !disabledRef.current && setLocationObject(parseGoogleMapLocation(point))}
              onPlaceSelected={place => !disabledRef.current && setLocationObject(parseGoogleMapPlace(place))}
            />
          </div>
        </>
      )}

      {selectedView === SAVED_LOCATION && (
        <>
          {coordinateFields}
          <SystemSelect
            disabled={disabled}
            value={selectedAddressId}
            label={t('GENERAL.chooseSavedAddress')}
            none={false}
            options={homeAddresses.map(a => ({
              label: [a.label || a.recipientName, a.line1, a.city].filter(Boolean).join(' — ') || a.line1,
              value: a.id,
            }))}
            onChange={val => {
              const addr = homeAddresses.find(a => a.id === val);
              if (!addr) return;
              setSelectedAddressId(val);
              setLocationObject({
                latitude: addr.latitude || null,
                longitude: addr.longitude || null,
                address: [addr.line1, addr.line2, addr.city, addr.country].filter(Boolean).join(', '),
              });
            }}
          />
        </>
      )}

      {/* SystemCheckbox is controlled and unnamed, so the legacy `propagateLocation` field
          is submitted through a hidden input while it's ticked (legacy CheckBox: same name,
          unticked by default). */}
      {showCheckBox && (
        <>
          <SystemCheckbox label={checkBoxLabel} checked={propagate} onChange={setPropagate} disabled={disabled} />
          {propagate && <HiddenField name="propagateLocation" value={true} />}
        </>
      )}
    </div>
  );
};

export default SystemLocationPicker;
